import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Clock, TrendingDown, TrendingUp, CheckCircle, Info, Undo } from 'lucide-react';
import SprintAPIService from '../../services/SprintAPIService';
import { toast } from 'react-hot-toast';

/**
 * RecoveryPlanModal
 *
 * Displays recovery options when student falls behind schedule.
 * Shows situation analysis, ranked recovery strategies with pros/cons,
 * and preview of changes.
 */
const RecoveryPlanModal = ({ isOpen, onClose, userId, triggerAlertId = null, onPlanApplied }) => {
  const [loading, setLoading] = useState(false);
  const [recoveryPlan, setRecoveryPlan] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [applying, setApplying] = useState(false);
  const [appliedResult, setAppliedResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [undoing, setUndoing] = useState(false);

  useEffect(() => {
    if (isOpen && userId) {
      loadRecoveryPlan();
    }
  }, [isOpen, userId, triggerAlertId]);

  const loadRecoveryPlan = async () => {
    setLoading(true);
    try {
      console.log('[RecoveryPlanModal] Generating recovery plan for user:', userId);
      const plan = await SprintAPIService.generateRecoveryPlan(userId, triggerAlertId);
      console.log('[RecoveryPlanModal] Recovery plan received:', plan);

      setRecoveryPlan(plan);

      // Auto-select recommended option
      const recommended = plan.recovery_options?.find(opt => opt.is_recommended);
      if (recommended) {
        setSelectedOption(recommended);
      }

      // Show warning if no options generated
      if (!plan.recovery_options || plan.recovery_options.length === 0) {
        toast.error('No recovery options available. You may be on track, or there may be no optional sprints to adjust. Check console for details.');
      }
    } catch (error) {
      console.error('[RecoveryPlanModal] Error loading recovery plan:', error);

      // Provide helpful error message based on the error
      if (error.message?.includes('No progress snapshot available')) {
        toast.error('Sprint schedule not found. Please apply the database migration and run the seed script first.');
      } else if (error.message?.includes('No sprint schedule found')) {
        toast.error('No sprint schedule exists yet. Please run the seed script to create one.');
      } else {
        toast.error(`Failed to generate recovery plan: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApplyOption = async () => {
    if (!selectedOption) return;

    setApplying(true);
    try {
      const result = await SprintAPIService.applyRecoveryOption(userId, selectedOption.id);

      console.log('[RecoveryPlanModal] Recovery option applied:', result);

      // Store result and show it to user
      setAppliedResult({
        ...result,
        recoveryPlanId: recoveryPlan.id,
        optionType: selectedOption.type,
        optionTitle: selectedOption.title
      });
      setShowResultModal(true);

      toast.success('✅ Recovery plan applied!');

      // Notify parent component
      if (onPlanApplied) {
        onPlanApplied(result);
      }
    } catch (error) {
      console.error('[RecoveryPlanModal] Error applying recovery option:', error);
      toast.error('Failed to apply recovery plan');
    } finally {
      setApplying(false);
    }
  };

  const handleUndo = async () => {
    if (!appliedResult?.recoveryPlanId) return;

    setUndoing(true);
    try {
      const result = await SprintAPIService.undoRecoveryPlan(userId, appliedResult.recoveryPlanId);

      console.log('[RecoveryPlanModal] Recovery plan undone:', result);

      toast.success(`✅ Undone! Restored ${result.sprints_restored} sprint(s)`);

      // Close both modals
      setShowResultModal(false);
      onClose();

      // Notify parent to refresh
      if (onPlanApplied) {
        onPlanApplied(result);
      }
    } catch (error) {
      console.error('[RecoveryPlanModal] Error undoing recovery plan:', error);
      toast.error('Failed to undo recovery plan');
    } finally {
      setUndoing(false);
    }
  };

  if (!isOpen) return null;

  const analysis = recoveryPlan?.situation_analysis || {};
  const options = recoveryPlan?.recovery_options || [];

  const getSeverityColor = (riskLevel) => {
    switch (riskLevel) {
      case 'EMERGENCY':
        return 'red';
      case 'CRITICAL':
        return 'orange';
      case 'AT_RISK':
        return 'yellow';
      default:
        return 'teal';
    }
  };

  const getRecoveryTypeIcon = (type) => {
    switch (type) {
      case 'COMPRESS_SPRINT':
        return '⚡';
      case 'SKIP_OPTIONAL':
        return '⏭️';
      case 'EXTEND_DAY':
        return '🌙';
      case 'SHIFT_DEADLINE':
        return '📅';
      case 'EMERGENCY_TRIAGE':
        return '🚨';
      default:
        return '🔧';
    }
  };

  const getEffortBadgeColor = (effort) => {
    switch (effort) {
      case 'NONE':
        return 'bg-teal-100 text-teal-800 border-teal-300';
      case 'LOW':
        return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'HIGH':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-300';
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border-2 border-slate-900 m-4">
        {/* Header */}
        <div className={`bg-gradient-to-r from-${getSeverityColor(analysis.risk_level)}-500 to-${getSeverityColor(analysis.risk_level)}-600 px-6 py-4 flex items-center justify-between border-b-2 border-slate-900`}>
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-white" />
            <div>
              <h2 className="text-xl font-bold text-white">Recovery Plan Generated</h2>
              <p className="text-sm text-white/90">
                {analysis.sprints_behind} sprint{analysis.sprints_behind !== 1 ? 's' : ''} behind schedule
                {analysis.minutes_behind > 0 && ` (${analysis.minutes_behind} min)`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            disabled={applying}
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-180px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
            </div>
          ) : (
            <>
              {/* Situation Analysis */}
              <div className="p-6 border-b border-slate-200 bg-slate-50">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Info className="w-5 h-5" />
                  Current Situation
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-slate-200">
                    <div className="text-sm text-slate-600 mb-1">Time Remaining</div>
                    <div className="text-2xl font-bold text-slate-900">
                      {Math.floor(analysis.hours_remaining || 0)}h
                    </div>
                    <div className="text-xs text-slate-500">
                      {analysis.days_remaining || 0} day{analysis.days_remaining !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-slate-200">
                    <div className="text-sm text-slate-600 mb-1">Sprints Left</div>
                    <div className="text-2xl font-bold text-slate-900">
                      {analysis.incomplete_sprints || 0}
                    </div>
                    <div className="text-xs text-slate-500">
                      ~{analysis.time_remaining || 0} min
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-slate-200">
                    <div className="text-sm text-slate-600 mb-1">Behind By</div>
                    <div className="text-2xl font-bold text-orange-600">
                      {analysis.sprints_behind || 0}
                    </div>
                    <div className="text-xs text-slate-500">sprints</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-slate-200">
                    <div className="text-sm text-slate-600 mb-1">Risk Level</div>
                    <div className={`text-lg font-bold text-${getSeverityColor(analysis.risk_level)}-600`}>
                      {analysis.risk_level || 'UNKNOWN'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Recovery Options */}
              <div className="p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Recovery Options</h3>

                {options.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-slate-200">
                    <p className="text-slate-600 mb-2">No recovery options available.</p>
                    <p className="text-sm text-slate-500">
                      This may occur if there are no optional sprints to skip or compress.
                      Check the console for more details.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {options.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setSelectedOption(option)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        selectedOption?.id === option.id
                          ? 'border-slate-900 bg-slate-50 shadow-md'
                          : 'border-slate-200 hover:border-slate-400 hover:bg-slate-50'
                      }`}
                      disabled={applying}
                    >
                      <div className="flex items-start justify-between gap-4">
                        {/* Left: Option details */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-2xl">{getRecoveryTypeIcon(option.type)}</span>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-bold text-slate-900">{option.title}</h4>
                                {option.is_recommended && (
                                  <span className="px-2 py-0.5 text-xs font-bold bg-teal-100 text-teal-800 rounded-full border border-teal-300">
                                    RECOMMENDED
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-slate-600 mt-1">{option.description}</p>
                            </div>
                          </div>

                          {/* Metrics */}
                          <div className="flex flex-wrap gap-3 mt-3">
                            {option.time_saved > 0 && (
                              <div className="flex items-center gap-1 text-sm">
                                <Clock className="w-4 h-4 text-teal-600" />
                                <span className="font-medium text-teal-700">+{option.time_saved} min</span>
                              </div>
                            )}
                            {option.quality_impact !== 0 && (
                              <div className="flex items-center gap-1 text-sm">
                                {option.quality_impact > 0 ? (
                                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                                ) : (
                                  <TrendingDown className="w-4 h-4 text-orange-600" />
                                )}
                                <span className={`font-medium ${option.quality_impact > 0 ? 'text-emerald-700' : 'text-orange-700'}`}>
                                  {option.quality_impact > 0 ? '+' : ''}{(option.quality_impact * 100).toFixed(0)}% quality
                                </span>
                              </div>
                            )}
                            <span className={`px-2 py-1 text-xs font-medium rounded border ${getEffortBadgeColor(option.effort_required)}`}>
                              {option.effort_required} effort
                            </span>
                          </div>

                          {/* Tradeoffs */}
                          {option.tradeoffs && option.tradeoffs.length > 0 && (
                            <div className="mt-3 pl-4 border-l-2 border-slate-200">
                              <p className="text-xs font-medium text-slate-600 mb-1">Tradeoffs:</p>
                              <ul className="space-y-0.5">
                                {option.tradeoffs.map((tradeoff, idx) => (
                                  <li key={idx} className="text-xs text-slate-600">• {tradeoff}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>

                        {/* Right: Selection indicator */}
                        <div className="flex-shrink-0">
                          {selectedOption?.id === option.id && (
                            <div className="w-6 h-6 rounded-full bg-slate-900 flex items-center justify-center">
                              <CheckCircle className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 bg-slate-50 border-t-2 border-slate-900 flex items-center justify-between gap-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-700 hover:bg-slate-200 rounded-lg font-medium transition-colors"
            disabled={applying}
          >
            Cancel
          </button>
          <button
            onClick={handleApplyOption}
            disabled={!selectedOption || applying}
            className={`px-6 py-2 rounded-lg font-bold transition-all flex items-center gap-2 ${
              selectedOption && !applying
                ? 'bg-slate-900 text-white hover:bg-slate-800 shadow-md'
                : 'bg-slate-300 text-slate-500 cursor-not-allowed'
            }`}
          >
            {applying ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Applying...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                Apply Selected Plan
              </>
            )}
          </button>
        </div>
      </div>

      {/* Result Modal - Shows what was changed */}
      {showResultModal && appliedResult && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full border-2 border-slate-900 m-4">
            {/* Result Header */}
            <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-6 py-4 border-b-2 border-slate-900">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-white" />
                <div>
                  <h2 className="text-xl font-bold text-white">Recovery Plan Applied</h2>
                  <p className="text-sm text-white/90">{appliedResult.optionTitle}</p>
                </div>
              </div>
            </div>

            {/* Result Content */}
            <div className="p-6">
              {/* What Changed */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-slate-900 mb-3">What Changed:</h3>

                {appliedResult.action === 'skip_optional' && appliedResult.sprint_names && (
                  <div className="space-y-2">
                    <p className="text-slate-700 mb-3">
                      The following {appliedResult.sprints_skipped} sprint(s) have been skipped:
                    </p>
                    <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
                      <ul className="space-y-2">
                        {appliedResult.sprint_names.map((name, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-slate-700">
                            <div className="w-1.5 h-1.5 rounded-full bg-coral-500"></div>
                            <span className="font-medium">{name}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-sm text-teal-700">
                      <Clock className="w-4 h-4" />
                      <span className="font-medium">Time saved: {appliedResult.time_saved} minutes</span>
                    </div>
                  </div>
                )}

                {appliedResult.action === 'compress_sprint' && (
                  <div className="space-y-2">
                    <p className="text-slate-700 mb-3">
                      Sprint compressed: <span className="font-bold">{appliedResult.sprint_name}</span>
                    </p>
                    <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
                      <p className="text-slate-700">
                        {appliedResult.tasks_skipped} optional task(s) removed from this sprint
                      </p>
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-sm text-teal-700">
                      <Clock className="w-4 h-4" />
                      <span className="font-medium">Time saved: {appliedResult.time_saved} minutes</span>
                    </div>
                  </div>
                )}

                {appliedResult.action === 'add_evening_sprint' && (
                  <div className="space-y-2">
                    <p className="text-slate-700 mb-3">
                      New evening sprint created: <span className="font-bold">{appliedResult.sprint_name}</span>
                    </p>
                    <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
                      <p className="text-slate-700">
                        {appliedResult.tasks_added} task(s) added
                      </p>
                      <p className="text-sm text-slate-600 mt-2">
                        Duration: {appliedResult.estimated_duration} minutes
                      </p>
                    </div>
                  </div>
                )}

                {appliedResult.action === 'emergency_triage' && (
                  <div className="space-y-2">
                    <p className="text-slate-700 mb-3 font-medium">
                      Focus mode activated - keeping top priorities only
                    </p>
                    <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-4 mb-3">
                      <p className="text-sm text-slate-600 mb-2">Schools kept:</p>
                      <ul className="space-y-1">
                        {appliedResult.schools_kept_names?.map((name, idx) => (
                          <li key={idx} className="text-slate-700 font-medium">✓ {name}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
                      <p className="text-sm text-slate-600 mb-2">Schools deferred to RD:</p>
                      <ul className="space-y-1">
                        {appliedResult.schools_deferred_names?.map((name, idx) => (
                          <li key={idx} className="text-slate-600">→ {name}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              {/* Undo Warning */}
              <div className="bg-gold-50 border border-gold-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-slate-700">
                  <span className="font-bold">Note:</span> If you changed your mind, you can undo this action below.
                  All skipped sprints will be restored to your schedule.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowResultModal(false);
                    onClose();
                  }}
                  className="flex-1 bg-slate-900 text-white rounded-lg px-6 py-3 font-bold hover:bg-slate-800 transition-all"
                  disabled={undoing}
                >
                  Done
                </button>
                <button
                  onClick={handleUndo}
                  disabled={undoing}
                  className="bg-white text-slate-900 border-2 border-slate-900 rounded-lg px-6 py-3 font-bold hover:bg-slate-50 transition-all flex items-center gap-2"
                >
                  {undoing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-900"></div>
                      Undoing...
                    </>
                  ) : (
                    <>
                      <Undo className="w-4 h-4" />
                      Undo This
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecoveryPlanModal;
